/**
 * Explore chart data prep (POS2-883) — pure functions between the engine's pivot
 * (POS2-882) and the Line/Bar views. Kept out of the React components so the
 * series-building and axis/tooltip formatting are unit-testable (the stacked-bar
 * sum vs. the engine's row totals is the load-bearing test) and shared verbatim by
 * both views + the Pie/Table pair (POS2-884).
 *
 * Two kinds of date formatting live here and they must not be confused. `localDay` produces a
 * *bucket key* matched against the RPC's `date_trunc` and is pinned to `en-CA` forever. The three
 * `formatBucket*` functions produce *labels a person reads* and take the reader's locale, like
 * every other display formatter (POS2-1134 CP4b). The ban on importing `lib/format.ts` was
 * originally written against this whole file; it belongs to `localDay` alone.
 */
import { format, parseISO } from "date-fns";

import type { Measure } from "@/hooks/use-explore-state";
import type { ExploreWindow, Granularity } from "@/lib/explore-window";
import {
  EXPLORE_MEASURES,
  computeDelta,
  type ExplorePivot,
  type MeasureValues,
} from "@/lib/explore-engine";
import type { Locale } from "@/i18n/config";
import type { TFunction } from "@/i18n/catalog";
import { dateFnsLocale, formatDate } from "@/lib/format";

/** Axis-tick date shapes as `Intl` field sets rather than pattern strings, so each locale supplies
 * its own order and punctuation — German writes "6. Juli" where English writes "6 Jul", and a
 * hand-written pattern can only produce one of them (POS2-1134 CP4b). */
const DAY: Intl.DateTimeFormatOptions = { day: "numeric" };
const MONTH: Intl.DateTimeFormatOptions = { month: "short" };
const MONTH_YEAR: Intl.DateTimeFormatOptions = { month: "short", year: "numeric" };
const DAY_MONTH: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
const DAY_MONTH_YEAR2: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "2-digit",
};

/** What a date label needs: the reader's locale and the catalogue. `MeasureContext` satisfies it
 * structurally, so a view passes the context it already holds and the tooltip — which has no
 * currency — passes the two fields it has. */
type LabelContext = { locale: Locale; t: TFunction };

const DAY_MS = 86_400_000;
const ZERO_MEASURE: MeasureValues = { cost: 0, weight: 0, count: 0 };

/**
 * The 8-colour series palette (design mock §6). Literal hex, NOT `--chart-*` oklch
 * CSS variables, so PNG export (POS2-885) serialises the theming intact. Series `i`
 * takes `EXPLORE_PALETTE[i % 8]`.
 */
export const EXPLORE_PALETTE = [
  "#2563eb",
  "#15803d",
  "#b45309",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#64748b",
  "#ca8a04",
] as const;

export type ChartSeries = { key: string; label: string; color: string };
export type ChartRow = { key: string; [seriesKey: string]: string | number };
export type ExploreChartData = {
  /** One row per time bucket (pivot.rowKeys order), each series keyed by its col key. */
  rows: ChartRow[];
  /** Series in pivot.colKeys order (window-cost desc, remainder last), coloured by index. */
  series: ChartSeries[];
  /** A single series (split === "none", or a split that collapsed to one) → area fill. */
  unsplit: boolean;
};

/**
 * Flatten the pivot into Recharts rows + coloured series for the active measure.
 * Every col key becomes a series so a stacked bar sums to `pivot.rowTotals` exactly
 * (the remainder tail included — dropping it would break the sum-to-total contract).
 * `rowKeys` defaults to the pivot's sparse buckets; the views pass the densified full
 * window (`windowRowKeys`) so the series spans the whole axis with zero-filled gaps.
 */
export function buildChartData(
  pivot: ExplorePivot,
  measure: Measure,
  rowKeys: string[] = pivot.rowKeys,
): ExploreChartData {
  const accessor = EXPLORE_MEASURES[measure].accessor;
  // Colour by the full colKeys index (so it's stable with Pie/Table + across measures),
  // then drop series that are 0 across the whole window for this measure — a flat-zero
  // line / empty bar is noise (POS2-1032). 0 everywhere → 0 contribution, so stacked
  // sums are unchanged.
  const series: ChartSeries[] = pivot.colKeys
    .map((key, i) => ({
      key,
      label: pivot.labels[key] ?? key,
      color: EXPLORE_PALETTE[i % EXPLORE_PALETTE.length],
    }))
    .filter((s) => accessor(pivot.colTotals[s.key] ?? ZERO_MEASURE) !== 0);
  const rows: ChartRow[] = rowKeys.map((rowKey) => {
    const row: ChartRow = { key: rowKey };
    for (const s of series) row[s.key] = accessor(pivot.cell(rowKey, s.key));
    return row;
  });
  return { rows, series, unsplit: series.length <= 1 };
}

/** A UTC instant's calendar day in `timeZone`, as "YYYY-MM-DD" (en-CA is ISO order). */
function localDay(instant: Date, timeZone: string): string {
  // Machine formatter — never locale-aware (POS2-1133). Mirrors `zonedDay`; the output is a
  // bucket key matched against the RPC's `date_trunc`, so a localised order breaks every chart.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant);
}

/** "YYYY-MM-DD" → UTC-midnight ms, so day/week stepping is a plain +N·DAY_MS with no
 * DST drift (the labels are calendar dates, matched back to the RPC's date_trunc). */
function dayToUtcMs(day: string): number {
  const [y, m, d] = day.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

/**
 * The ordered bucket keys spanning a window, generated in the SITE timezone so they
 * match the RPC's `time_key`s exactly (the RPC truncates `logged_at AT TIME ZONE
 * p_timezone`). Format mirrors the RPC: daily/weekly "YYYY-MM-DD" (weekly = the ISO
 * Monday), monthly "YYYY-MM".
 */
function windowBucketKeys(window: ExploreWindow, gran: Granularity, timeZone: string): string[] {
  // The window is [from, to); the last bucket is the one holding `to − 1ms` (so a
  // boundary — e.g. the current month in the 12-full-month default — isn't generated).
  const startDay = localDay(window.from, timeZone);
  const endDay = localDay(new Date(window.to.getTime() - 1), timeZone);
  if (endDay < startDay) return [];

  if (gran === "monthly") {
    const keys: string[] = [];
    let [y, m] = startDay.split("-").map(Number); // m is 1-12
    const [ey, em] = endDay.split("-").map(Number);
    while (y < ey || (y === ey && m <= em)) {
      keys.push(`${y}-${String(m).padStart(2, "0")}`);
      if (++m > 12) {
        m = 1;
        y++;
      }
    }
    return keys;
  }

  const endMs = dayToUtcMs(endDay);
  let ms = dayToUtcMs(startDay);
  if (gran === "weekly") {
    // Back up to the ISO Monday of the start day's week (Sun=0 → 6 days back).
    ms -= ((new Date(ms).getUTCDay() + 6) % 7) * DAY_MS;
  }
  const step = gran === "weekly" ? 7 * DAY_MS : DAY_MS;
  const keys: string[] = [];
  for (; ms <= endMs; ms += step) keys.push(new Date(ms).toISOString().slice(0, 10));
  return keys;
}

/**
 * Densify the pivot's sparse buckets across the whole window so the time series spans
 * the full axis (zero-filled gaps): a recent-only dataset draws a line instead of
 * collapsing to a single bucket (and contiguous days let the weekend shading place its
 * bands). Buckets are generated in the site `timeZone` to line up with the RPC; every
 * server key is still kept as a backstop, so data can never drop on a timezone edge.
 */
export function windowRowKeys(
  pivot: ExplorePivot,
  window: ExploreWindow,
  gran: Granularity,
  timeZone: string,
): string[] {
  const keys = new Set(windowBucketKeys(window, gran, timeZone));
  for (const k of pivot.rowKeys) keys.add(k);
  return [...keys].sort();
}

/**
 * Does the window hold a single bucket? A line needs two points, so the Line view renders a Bar
 * instead (POS2-1050). Two callers must agree on the answer: the Line view, which does the
 * substituting, and the panel header, which captions it — so the rule lives here rather than as
 * a `< 2` in each.
 */
export function isSingleBucketWindow(
  pivot: ExplorePivot,
  window: ExploreWindow,
  gran: Granularity,
  timeZone: string,
): boolean {
  return windowRowKeys(pivot, window, gran, timeZone).length < 2;
}

/**
 * How much calendar the plotted buckets cover. The tick labels are written for a short window —
 * a bare day number only reads inside one month, a bare month name only inside one year — so
 * past that the tick has to say which (POS2-1068: a 2022–2026 daily view labelled its axis
 * "1, 10, 16, 23, 1, 8…", and the monthly one "Jan, Jun, Nov, Apr…").
 */
export type TickSpan = "month" | "year" | "years";

export function tickSpan(keys: string[]): TickSpan {
  if (keys.length === 0) return "month";
  const first = parseISO(keys[0]);
  const last = parseISO(keys[keys.length - 1]);
  if (first.getFullYear() !== last.getFullYear()) return "years";
  return first.getMonth() === last.getMonth() ? "month" : "year";
}

/**
 * Axis tick label, widened to the span: Monthly "Jun" → "Jun 2024"; Weekly "23 Jun" →
 * "23 Jun 24"; Daily "23" → "23 Jun" → "23 Jun 24". The daily tick draws the weekday initial
 * above the day, but only while the window sits inside one month (see BucketTick).
 *
 * The pattern is a machine instruction; the language its month names come out in is the
 * `locale` option (POS2-1134 CP4b). This is a *display* formatter and takes a locale — unlike
 * `localDay` below, which is pinned because its output is a bucket key.
 */
export function formatBucketTick(
  key: string,
  gran: Granularity,
  span: TickSpan,
  { locale }: LabelContext,
): string {
  const d = parseISO(key);
  const at = (options: Intl.DateTimeFormatOptions) => formatDate(locale, d, options);
  if (gran === "monthly") return at(span === "years" ? MONTH_YEAR : MONTH);
  if (gran === "weekly") return at(span === "years" ? DAY_MONTH_YEAR2 : DAY_MONTH);
  if (span === "years") return at(DAY_MONTH_YEAR2);
  return span === "year" ? at(DAY_MONTH) : at(DAY);
}

/** Tooltip / Table-row label: Monthly "Jun 2026"; Weekly "w/c 23 Jun 2026"; Daily "Mon 23 Jun
 * 2026" (abbreviated weekday — the full name is too wide for a dense daily table, Artem).
 *
 * The weekly form is a sentence, not a pattern — "w/c" is an English abbreviation for "week
 * commencing" and German writes "Woche ab" — so it comes from the catalogue. */
export function formatBucketLong(key: string, gran: Granularity, ctx: LabelContext): string {
  const d = parseISO(key);
  const opts = { locale: dateFnsLocale(ctx.locale) };
  if (gran === "monthly") return format(d, "MMM yyyy", opts);
  // `PP` is date-fns's *localised* medium date rather than a hand-written field order: en-GB
  // "6 Jul 2026", de "6. Juli 2026". A literal "d MMM yyyy" would drop the ordinal dot German
  // requires, and the English output is byte-identical either way (POS2-1134 CP4b).
  if (gran === "weekly") return ctx.t("waste.weekCommencing", { date: format(d, "PP", opts) });
  return format(d, "EEE PP", opts);
}

/** Thin the axis toward ~13 labels so a long daily window stays legible — Recharts draws every
 * (interval + 1)-th tick, so a full-year daily view (365 buckets) would otherwise overprint every
 * other label into an unreadable smear. All ticks show up to ~13 buckets. */
export function tickInterval(bucketCount: number): number {
  const TARGET_LABELS = 13;
  return bucketCount <= TARGET_LABELS ? 0 : Math.ceil(bucketCount / TARGET_LABELS) - 1;
}

// --- Pie + Table (POS2-884) -------------------------------------------------

export type PieSlice = {
  key: string;
  label: string;
  color: string;
  value: number;
  /** Fraction of the window total (0–1); shares sum to 1 because the remainder is a slice. */
  share: number;
};

/**
 * Donut data: one slice per split column (pivot.colKeys order = cost desc, remainder last),
 * valued by the active measure. Denominator is the window grand total, and the remainder is a
 * slice, so the shares sum to 1 (±rounding). Only meaningful when split ≠ none.
 */
export function buildPieData(
  pivot: ExplorePivot,
  measure: Measure,
): { slices: PieSlice[]; total: number } {
  const accessor = EXPLORE_MEASURES[measure].accessor;
  const total = accessor(pivot.grandTotal);
  const slices: PieSlice[] = pivot.colKeys.map((key, i) => {
    const value = accessor(pivot.colTotals[key] ?? ZERO_MEASURE);
    return {
      key,
      label: pivot.labels[key] ?? key,
      color: EXPLORE_PALETTE[i % EXPLORE_PALETTE.length],
      value,
      share: total > 0 ? value / total : 0,
    };
  });
  return { slices, total };
}

export type TableMatrix = {
  /** Split columns (empty when unsplit — then the single value column is the row total). */
  columns: { key: string; label: string }[];
  rows: { key: string; label: string; cells: number[]; total: number }[];
  /** Per-column window totals, parallel to `columns` (empty when unsplit). */
  colTotals: number[];
  grandTotal: number;
  unsplit: boolean;
};

/**
 * Pivot matrix for the Table: one row per populated time bucket (pivot.rowKeys — sparse, like the
 * mock), split columns in cost-desc order, valued by the active measure. Row/column/grand totals
 * come straight from the engine's margins, so they agree with the chart views by construction.
 */
export function buildTableMatrix(
  pivot: ExplorePivot,
  measure: Measure,
  gran: Granularity,
  ctx: LabelContext,
): TableMatrix {
  const accessor = EXPLORE_MEASURES[measure].accessor;
  const unsplit = pivot.colKeys.length <= 1;
  const columns = unsplit
    ? []
    : pivot.colKeys.map((k) => ({ key: k, label: pivot.labels[k] ?? k }));
  const rows = pivot.rowKeys.map((rk) => ({
    key: rk,
    label: formatBucketLong(rk, gran, ctx),
    cells: unsplit ? [] : pivot.colKeys.map((ck) => accessor(pivot.cell(rk, ck))),
    total: accessor(pivot.rowTotals[rk] ?? ZERO_MEASURE),
  }));
  const colTotals = unsplit
    ? []
    : pivot.colKeys.map((ck) => accessor(pivot.colTotals[ck] ?? ZERO_MEASURE));
  return { columns, rows, colTotals, grandTotal: accessor(pivot.grandTotal), unsplit };
}

// --- Period-over-period delta (POS2-1038) -----------------------------------

/** The window's inclusive civil-day span in `timeZone` ("YYYY-MM-DD" bounds). `to − 1ms`
 * gives the last included day for either window.to convention (exclusive midnight default,
 * inclusive last-ms custom). */
function windowDayBounds(
  window: ExploreWindow,
  timeZone: string,
): { firstDay: string; lastDay: string } {
  return {
    firstDay: localDay(window.from, timeZone),
    lastDay: localDay(new Date(window.to.getTime() - 1), timeZone),
  };
}

/** Whether a bucket's whole period sits inside the window's civil-day span, so a
 * period-over-period delta compares like with like. Daily is always full (the window is
 * day-aligned); a weekly/monthly bucket clipped by the window start/end is partial → its
 * delta is suppressed rather than comparing a part-period against a whole one. */
export function isFullPeriod(
  bucketKey: string,
  gran: Granularity,
  firstDay: string,
  lastDay: string,
): boolean {
  if (gran === "monthly") {
    const [y, m] = bucketKey.split("-").map(Number);
    const first = `${bucketKey}-01`;
    const last = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10); // day 0 = last of month
    return first >= firstDay && last <= lastDay;
  }
  const last =
    gran === "weekly"
      ? new Date(dayToUtcMs(bucketKey) + 6 * DAY_MS).toISOString().slice(0, 10)
      : bucketKey;
  return bucketKey >= firstDay && last <= lastDay;
}

/** The bucket immediately before `window`'s first bucket — the last bucket of the preceding
 * `priorWindow` — but only when that period sits fully inside `priorWindow`, so the first row's Δ
 * still compares whole-period against whole-period (POS2-1038). Null otherwise. */
function leadingKey(
  priorWindow: ExploreWindow,
  gran: Granularity,
  timeZone: string,
): string | null {
  const keys = windowBucketKeys(priorWindow, gran, timeZone);
  const key = keys[keys.length - 1];
  if (key === undefined) return null;
  const { firstDay, lastDay } = windowDayBounds(priorWindow, timeZone);
  return isFullPeriod(key, gran, firstDay, lastDay) ? key : null;
}

/** Per-bucket Δ on the row total vs the PREVIOUS period — the adjacent bucket in the densified
 * window, not the previous populated row — the single source for the Table view's Δ column. Null
 * when the previous period is zero/absent (computeDelta's floor) or a partial edge bucket. Pass
 * `priorPivot` + `priorWindow` (the preceding window) to give the FIRST bucket a baseline from the
 * period just before the window (POS2-1038); without them the first bucket is always null. */
export function rowTotalDeltas(
  pivot: ExplorePivot,
  measure: Measure,
  window: ExploreWindow,
  gran: Granularity,
  timeZone: string,
  priorPivot: ExplorePivot | null = null,
  priorWindow: ExploreWindow | null = null,
): Map<string, number | null> {
  const accessor = EXPLORE_MEASURES[measure].accessor;
  const keys = windowBucketKeys(window, gran, timeZone);
  const { firstDay, lastDay } = windowDayBounds(window, timeZone);
  const total = (k: string) => accessor(pivot.rowTotals[k] ?? ZERO_MEASURE);
  const leadKey = priorPivot && priorWindow ? leadingKey(priorWindow, gran, timeZone) : null;
  const out = new Map<string, number | null>();
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const curFull = isFullPeriod(key, gran, firstDay, lastDay);
    let prev: number | null;
    let prevFull: boolean;
    if (i > 0) {
      prev = total(keys[i - 1]);
      prevFull = isFullPeriod(keys[i - 1], gran, firstDay, lastDay);
    } else if (leadKey && priorPivot) {
      prev = accessor(priorPivot.rowTotals[leadKey] ?? ZERO_MEASURE);
      prevFull = true; // leadingKey already checked the prior period is full
    } else {
      prev = null;
      prevFull = false;
    }
    out.set(key, curFull && prevFull ? computeDelta(total(key), prev) : null);
  }
  return out;
}

/** Per-cell Δ for the Table: each split column's value vs the SAME column in the previous period
 * (the adjacent densified bucket), suppressed on partial edges — the per-series analogue of
 * `rowTotalDeltas`, agreeing with the Line/Bar tooltip's per-series delta by construction. Keyed
 * `${rowKey}|${colKey}`; null when the previous period is zero/absent or a partial edge. Pass
 * `priorPivot` + `priorWindow` to give the FIRST bucket a per-column baseline (POS2-1038). */
export function cellDeltas(
  pivot: ExplorePivot,
  measure: Measure,
  window: ExploreWindow,
  gran: Granularity,
  timeZone: string,
  priorPivot: ExplorePivot | null = null,
  priorWindow: ExploreWindow | null = null,
): Map<string, number | null> {
  const accessor = EXPLORE_MEASURES[measure].accessor;
  const keys = windowBucketKeys(window, gran, timeZone);
  const { firstDay, lastDay } = windowDayBounds(window, timeZone);
  const leadKey = priorPivot && priorWindow ? leadingKey(priorWindow, gran, timeZone) : null;
  const out = new Map<string, number | null>();
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const prevKey = i > 0 ? keys[i - 1] : null;
    const curFull = isFullPeriod(key, gran, firstDay, lastDay);
    const prevFull = prevKey ? isFullPeriod(prevKey, gran, firstDay, lastDay) : leadKey !== null;
    for (const col of pivot.colKeys) {
      const prev =
        prevKey !== null
          ? accessor(pivot.cell(prevKey, col))
          : leadKey && priorPivot
            ? accessor(priorPivot.cell(leadKey, col))
            : 0;
      out.set(
        `${key}|${col}`,
        curFull && prevFull ? computeDelta(accessor(pivot.cell(key, col)), prev) : null,
      );
    }
  }
  return out;
}
